import { type ReactNode, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { Toaster } from "sonner";

import { cn } from "@/lib/cn";
import { READONLY } from "@/lib/config";
import {
  Building,
  Check,
  ChevronCollapse,
  ChevronExpand,
  Copy,
  Database,
  Folder,
  Package,
} from "@/lib/icons";

const NAV_ALL = [
  { to: "/",           label: "Products",   Icon: Package   },
  { to: "/review",     label: "Review",     Icon: Check     },
  { to: "/duplicates", label: "Duplicates", Icon: Copy      },
  { to: "/batches",    label: "Batches",    Icon: Folder    },
  { to: "/buyers",     label: "Buyers",     Icon: Building  },
];

const NAV = READONLY
  ? NAV_ALL.filter((n) => n.to === "/")
  : NAV_ALL;

const COLLAPSED_KEY = "wb.sidebar.collapsed";

export function Layout({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  });

  useEffect(() => {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  return (
    <div className="flex min-h-screen">
      {!READONLY && <aside
        className={cn(
          "relative hidden shrink-0 border-r border-zinc-200 bg-white p-3 transition-[width] duration-200 ease-out md:block",
          collapsed ? "w-16" : "w-56",
        )}
      >
        <div className="mb-6 flex h-7 items-center gap-2 px-1">
          <Database className="size-5 shrink-0 text-indigo-600" />
          <span
            className={cn(
              "overflow-hidden whitespace-nowrap text-sm font-semibold tracking-tight transition-[opacity,max-width] duration-200",
              collapsed ? "max-w-0 opacity-0" : "max-w-[10rem] opacity-100",
            )}
          >
            warehouse-busters
          </span>
        </div>

        <nav className="flex flex-col gap-1">
          {NAV.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-2 rounded-md px-3 py-2 text-sm",
                  isActive
                    ? "bg-indigo-50 font-medium text-indigo-700"
                    : "text-zinc-700 hover:bg-zinc-50",
                  collapsed && "justify-center px-0",
                )
              }
            >
              <Icon className="size-4 shrink-0" />
              <span
                className={cn(
                  "overflow-hidden whitespace-nowrap transition-[opacity,max-width] duration-200",
                  collapsed ? "max-w-0 opacity-0" : "max-w-[10rem] opacity-100",
                )}
              >
                {label}
              </span>
            </NavLink>
          ))}
        </nav>

        <button
          type="button"
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          onClick={() => setCollapsed((v) => !v)}
          className="absolute -right-3 top-6 grid size-6 place-items-center rounded-full border border-zinc-200 bg-white text-zinc-500 shadow-sm transition-colors hover:bg-zinc-50 hover:text-zinc-800"
        >
          {collapsed ? <ChevronExpand className="size-3" /> : <ChevronCollapse className="size-3" />}
        </button>
      </aside>}
      <main className="min-w-0 flex-1 bg-white px-4 py-4 sm:px-6 sm:py-6">{children}</main>
      <Toaster position="bottom-right" richColors closeButton />
    </div>
  );
}
